/**
 * Shared client-side helper for kicking off the QuickBooks Online OAuth 2.0
 * flow.
 *
 * Used by:
 *   - IntegrationsPage's "Connect with OAuth" / "Reconnect" buttons.
 *   - The public /quickbooks/connect landing page Intuit redirects users to
 *     from the apps.com listing.
 *
 * Reads `VITE_QBO_CLIENT_ID` from the build-time env. Stores a CSRF state
 * token in `sessionStorage` and redirects the browser to Intuit's OAuth
 * authorize endpoint. The matching state is validated by `QboCallbackPage`
 * after Intuit redirects back.
 */

export const QBO_OAUTH_STATE_KEY = 'qbo_oauth_state';

export const QBO_REDIRECT_PATH = '/integrations/qbo/callback';

/**
 * Returns the redirect_uri that Intuit will send the user back to after
 * they grant or deny consent. This MUST be registered in the Intuit
 * Developer Dashboard under "Redirect URIs" and MUST match exactly the
 * value sent in both the auth request and the token-exchange request.
 */
export function qboRedirectUri(): string {
  return `${window.location.origin}${QBO_REDIRECT_PATH}`;
}

/**
 * Begins the OAuth flow by redirecting the browser to Intuit. Returns
 * `null` on success (the caller's tab is about to navigate away) and a
 * human-readable error string if the env isn't configured. Callers should
 * surface the error via toast/alert.
 */
export function startQboOAuth(): string | null {
  const clientId = import.meta.env.VITE_QBO_CLIENT_ID;
  if (!clientId) {
    return 'VITE_QBO_CLIENT_ID is not set. Add it to .env (it must match the QBO_CLIENT_ID server secret) and redeploy.';
  }

  const state = crypto.randomUUID();
  sessionStorage.setItem(QBO_OAUTH_STATE_KEY, state);

  const url = new URL('https://appcenter.intuit.com/connect/oauth2');
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'com.intuit.quickbooks.accounting');
  url.searchParams.set('redirect_uri', qboRedirectUri());
  url.searchParams.set('state', state);
  window.location.href = url.toString();
  return null;
}
